import { metrics } from 'works/legacy/lib/sentry';

import type { DomainNameType } from './subdomains';

// Every op the shadow surface can emit a counter for. The four construction ops
// (moment/tz/parseZone/duration) are emitted by the M3.1 runner; the rest are the
// chained-operation surface lit up by the M3.4 proxy (op-allowlist.ts), covering
// every moment instance method actually used across app/javascript (usage-scan
// derived, WORK-6305). Ops with no dayjs peer in the canonical datetime service
// (e.g. isBetween) are intentionally absent here — they stay in PASS_THROUGH.
export type MomentOpType =
  // construction entry points
  | 'moment'
  | 'tz'
  | 'parseZone'
  | 'duration'
  | 'updateLocale'
  // chaining ops (return a re-wrapped Moment)
  | 'utc'
  | 'local'
  | 'set'
  | 'add'
  | 'subtract'
  | 'startOf'
  | 'endOf'
  | 'clone'
  // terminal ops (return a scalar/string/boolean)
  | 'format'
  | 'valueOf'
  | 'unix'
  | 'toISOString'
  | 'diff'
  | 'isBefore'
  | 'isAfter'
  | 'isSame'
  | 'isSameOrBefore'
  | 'isSameOrAfter'
  | 'humanize'
  // dual getter/setter ops (arity-classified at call time)
  | 'year'
  | 'month'
  | 'date'
  | 'day'
  | 'weekday'
  | 'hour'
  | 'hours'
  | 'minute'
  | 'minutes'
  | 'second'
  | 'seconds'
  | 'millisecond'
  | 'milliseconds';

export type CounterTagsType = {
  domain: DomainNameType;
  subdomain: string;
  op: MomentOpType;
  match: 'true' | 'false';
};

// The plan doc refers to `Sentry.metrics.increment(name, value, { tags })` but
// @sentry/react@^10.53.1 exposes `count` + `attributes`. The wire shape
// downstream consumers see is identical — Sentry maps both onto the same
// trace_metric envelope — so the observation skill's tag-filter queries work
// against either naming. The plan doc will be amended separately.
export function emitShadowCallCounter(tags: CounterTagsType): void {
  metrics.count('moment_shadow.call', 1, { attributes: tags });
}
